import React from "react";
import { cva, VariantProps } from "class-variance-authority";
import clsx from "clsx";
import { HiXMark } from "react-icons/hi2";

import Button from "@/components/atoms/Button";

const chipVariants = cva(
  "py-1 inline-flex items-center justify-center font-medium rounded border-[0.5px] outline-none disabled-within:opacity-40 disabled-within:pointer-events-none disabled-within:select-none transition duration-75",
  {
    variants: {
      size: {
        xs: "text-xs px-1.5 gap-x-1",
        sm: "text-sm px-1.5 gap-x-1",
        md: "text-base px-2 gap-x-1.5",
      },
      color: {
        white: "bg-primary text-tertiary border-primary hover:text-tertiary-hover active:text-tertiary-pressed",
        natural: "bg-secondary text-tertiary border-primary hover:text-tertiary-hover active:text-tertiary-pressed",
        primary:
          "bg-brand-primary-subtle border-brand-primary-subtle text-brand-primary hover:text-brand-primary-hover active:text-brand-primary-pressed",
        secondary:
          "bg-brand-secondary-subtle text-brand-secondary border-brand-secondary-subtle hover:text-brand-secondary-hover active:text-brand-secondary-pressed",
        success:
          "bg-success-primary-subtle text-success border-success-subtle hover:text-success-hover active:text-success-pressed",
        warning:
          "bg-warning-primary-subtle text-warning border-warning-subtle hover:text-warning-hover active:text-warning-pressed",
        error:
          "bg-error-primary-subtle text-error border-error-subtle hover:text-error-hover active:text-error-pressed",
      },
    },
    defaultVariants: {
      size: "md",
      color: "primary",
    },
  },
);

type ChipProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof chipVariants> & {
    label: string | React.ReactNode;
    leadingIcon?: React.ReactNode;
    className?: string;
    onRemove?: () => void;
    disabled?: boolean;
  };

const Chip = ({ size, color, leadingIcon, onRemove, label, disabled, className, ...props }: ChipProps) => {
  return (
    <button className={clsx(chipVariants({ size, color }), className)} {...props} type={"button"} disabled={disabled}>
      {leadingIcon}
      <span className={"truncate"}>{label}</span>
      {onRemove && (
        <Button
          type={"button"}
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          data-testid={"remove-chip"}
          variants={"text"}
          color={"natural"}
          size={"sm"}
          className={"shrink-0 !rounded !p-0 outline-none focus-visible:shadow-xs focus-visible:!ring-2"}
          leadingIcon={<HiXMark className={size === "xs" ? "size-4" : "size-5"} />}
        />
      )}
    </button>
  );
};

export default Chip;
